import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  getActiveWorkspaceId,
  getWorkspace,
  setWorkspace,
  getWorkspaceSelectedAvatar,
  setActiveWorkspaceAvatar,
} from "../lib/workspaceStore";

export default function AvatarLibraryPage() {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [creator, setCreator] = useState({
    name: "",
    gender: "female",
    age_range: "28-38",
    ethnicity: "",
    industry: "marketing",
    tone: "confident",
    look_style: "brand strategist, refined, premium, calm authority",
    personality: "strategic, composed, persuasive",
    speaking_style: "clear, confident, polished",
    camera_presence: "premium in-house OmniMark spokesperson",
  });

  useEffect(() => {
    loadAvatars();
    loadSelectedAvatarFromWorkspace();

    const selected = getWorkspaceSelectedAvatar();
    if (selected?.id) {
      setSelectedAvatarId(selected.id);
    }
  }, []);

  async function loadAvatars() {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/avatars?limit=24&offset=0");
      setAvatars(res.data.items || []);
      setHasMore(Boolean(res.data.has_more));
      setOffset((res.data.items || []).length);
    } catch (err) {
      setError("Failed to load avatars.");
    } finally {
      setLoading(false);
    }
  }

  async function loadSelectedAvatarFromWorkspace() {
    const cachedWorkspace = getWorkspace();
    const rawWorkspace = cachedWorkspace ? JSON.stringify(cachedWorkspace) : null;
    if (!rawWorkspace) return;

    let activeWorkspaceId = null;

    try {
      const parsed = JSON.parse(rawWorkspace);
      if (parsed && typeof parsed === "object") {
        activeWorkspaceId = parsed.id || parsed.workspace_id || null;

        if (parsed.selected_avatar_id) {
          setSelectedAvatarId(parsed.selected_avatar_id);
          localStorage.setItem("selected_avatar_id", parsed.selected_avatar_id);
          localStorage.setItem("selected_avatar_name", parsed.selected_avatar_name || "");
          localStorage.setItem(
            "selected_avatar_image_url",
            parsed.selected_avatar_image_url || ""
          );
        }
      } else {
        activeWorkspaceId = rawWorkspace;
      }
    } catch (e) {
      activeWorkspaceId = rawWorkspace;
    }

    if (!activeWorkspaceId) return;

    try {
      const res = await api.get("/api/workspaces");
      const items = res.data.items || [];
      const activeWorkspace = items.find((w) => w.id === activeWorkspaceId);

      if (activeWorkspace?.selected_avatar_id) {
        setSelectedAvatarId(activeWorkspace.selected_avatar_id);
        localStorage.setItem("selected_avatar_id", activeWorkspace.selected_avatar_id);
        localStorage.setItem("selected_avatar_name", activeWorkspace.selected_avatar_name || "");
        localStorage.setItem(
          "selected_avatar_image_url",
          activeWorkspace.selected_avatar_image_url || ""
        );

        setWorkspace(activeWorkspace);
      }
    } catch (err) {
      console.error("Failed to load selected avatar from workspace", err);
    }
  }

  async function loadMoreAvatars() {
    setError("");
    try {
      const res = await api.get(`/api/avatars?limit=24&offset=${offset}`);
      const nextItems = res.data.items || [];
      setAvatars((current) => [...current, ...nextItems]);
      setHasMore(Boolean(res.data.has_more));
      setOffset((current) => current + nextItems.length);
    } catch (err) {
      setError("Failed to load more avatars.");
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    setError("");
    try {
      await api.post("/api/avatars/generate", {
        count: 3,
        industry: "marketing",
        style: "modern professional",
        tone: "confident",
      });
      await loadAvatars();
      setSuccess("Generated 3 new in-house avatars.");
    } catch (err) {
      setError("Failed to generate avatars.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleCreateCustomAvatar() {
    if (!creator.name.trim()) {
      setError("Add an avatar name first.");
      return;
    }

    setGenerating(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.post("/api/avatars/generate", {
        count: 1,
        name: creator.name.trim(),
        gender: creator.gender,
        age_range: creator.age_range,
        ethnicity: creator.ethnicity,
        industry: creator.industry,
        tone: creator.tone,
        look_style: creator.look_style,
        personality: creator.personality,
        speaking_style: creator.speaking_style,
        camera_presence: creator.camera_presence,
      });

      await loadAvatars();
      const created = res.data.items?.[0];
      setSuccess(`${created?.name || creator.name} created in your OmniMark Avatar Library.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create custom avatar.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleUseAvatar(avatar) {
    const workspaceId = getActiveWorkspaceId();

    if (!workspaceId) {
      alert("No active workspace selected.");
      return;
    }

    try {
      const res = await api.post("/api/workspaces/select-avatar", {
          workspace_id: workspaceId,
          avatar_id: avatar.id,
      });

      const data = res.data;

      // Save locally for immediate UI and AI Studio handoff.
      localStorage.setItem("selected_avatar_id", data.selected_avatar_id);
      localStorage.setItem("selected_avatar_name", data.selected_avatar_name);

      setSelectedAvatarId(data.selected_avatar_id);

      setActiveWorkspaceAvatar({
        id: data.selected_avatar_id,
        name: data.selected_avatar_name,
        image_url: data.selected_avatar_image_url || "",
      });

      setSuccess(`${data.selected_avatar_name} selected.`);
      console.log(`${data.selected_avatar_name} selected`);
    } catch (err) {
      console.error(err);
      alert("Failed to save avatar.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-semibold">Avatar Library</div>
          <div className="text-sm text-muted-foreground">
            Omnimark-generated AI avatars for campaigns, videos, and spokesperson content.
          </div>
        </div>

        <Button onClick={handleGenerate} disabled={generating}>
          {generating ? "Generating..." : "Generate 3 Avatars"}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle>Create In-house Avatar</CardTitle>
          <div className="text-sm text-muted-foreground">
            Design a custom OmniMark avatar for your brand, campaigns, product videos, and AI Studio scripts.
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Avatar name</label>
              <Input
                value={creator.name}
                onChange={(e) => setCreator((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Luna Growth"
                data-testid="avatar-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Gender presentation</label>
              <select
                value={creator.gender}
                onChange={(e) => setCreator((p) => ({ ...p, gender: e.target.value }))}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="person">Neutral</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Age range</label>
              <Input
                value={creator.age_range}
                onChange={(e) => setCreator((p) => ({ ...p, age_range: e.target.value }))}
                placeholder="28-38"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Ethnicity or visual identity</label>
              <Input
                value={creator.ethnicity}
                onChange={(e) => setCreator((p) => ({ ...p, ethnicity: e.target.value }))}
                placeholder="e.g., Black, Latina, East Asian, diverse"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Industry</label>
              <Input
                value={creator.industry}
                onChange={(e) => setCreator((p) => ({ ...p, industry: e.target.value }))}
                placeholder="marketing"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Voice tone</label>
              <Input
                value={creator.tone}
                onChange={(e) => setCreator((p) => ({ ...p, tone: e.target.value }))}
                placeholder="confident"
              />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Look and role style</label>
              <Textarea
                value={creator.look_style}
                onChange={(e) => setCreator((p) => ({ ...p, look_style: e.target.value }))}
                className="min-h-[96px]"
                placeholder="Describe wardrobe, job role, setting, posture, and visual personality."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Personality and camera presence</label>
              <Textarea
                value={`${creator.personality}\n${creator.camera_presence}`}
                onChange={(e) => {
                  const [personality, ...presence] = e.target.value.split("\n");
                  setCreator((p) => ({
                    ...p,
                    personality,
                    camera_presence: presence.join(" ").trim() || p.camera_presence,
                  }));
                }}
                className="min-h-[96px]"
                placeholder="Describe how the avatar should feel on camera."
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={handleCreateCustomAvatar} disabled={generating} data-testid="create-custom-avatar-btn">
              {generating ? "Creating Avatar..." : "Create In-house Avatar"}
            </Button>
            <Button variant="outline" onClick={handleGenerate} disabled={generating}>
              Generate 3 Ready-made Avatars
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading avatars...</div>
      ) : avatars.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No avatars yet. Generate your first OmniMark avatars.
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {avatars.map((avatar) => (
            <Card key={avatar.id}>
              <CardHeader>
                <CardTitle>{avatar.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(avatar.avatar_image_url || avatar.image_url) && (
                  <div className="w-full h-64 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
                    <img
                      src={avatar.avatar_image_url || avatar.image_url || ""}
                      alt={avatar.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}

                <div><span className="font-medium">Tone:</span> {avatar.voice_tone || "-"}</div>
                <div><span className="font-medium">Style:</span> {avatar.look_style || "-"}</div>
                <div><span className="font-medium">Industry:</span> {avatar.industry_fit?.join(", ") || "-"}</div>
                <div><span className="font-medium">Personality:</span> {avatar.personality || "-"}</div>
                <div><span className="font-medium">Source:</span> {avatar.source || "-"}</div>

                <Button
                  onClick={() => handleUseAvatar(avatar)}
                  className="w-full mt-3"
                  variant={selectedAvatarId === avatar.id ? "default" : "outline"}
                >
                  {selectedAvatarId === avatar.id ? "Selected Avatar" : "Use Avatar"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        {hasMore && (
          <div className="flex justify-center pt-2">
            <Button variant="outline" onClick={loadMoreAvatars}>Load more avatars</Button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
